import { getClient } from '@/api/api-client';
import { fetchRentalContracts } from '@/api/renting.api';
import { type IRentalContract, RentalContract } from '@condo/domain';
import type { BaseRentalContract, FindRentalContractsQueryParams, PatchRentalContractMutationRequest, RentalContractExpandOpts } from '@condo/sdk/propx';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { type MaybeRef, unref } from 'vue';
import { computed } from 'vue';

type RentalContractQuery = FindRentalContractsQueryParams['query'];
type RentalContractQueryParam = number | RentalContractQuery | undefined;

export const useEstateRentalContracts = (
    queryOrEstateId: MaybeRef<RentalContractQueryParam>,
    expandOpts?: RentalContractExpandOpts,
    options?: { enabled?: MaybeRef<boolean> },
) => {
    const resolvedQuery = computed<RentalContractQuery | undefined>(() => {
        const val = unref(queryOrEstateId);
        if (typeof val === 'number') {
            return { estateId: val };
        }
        return val;
    });

    const defaultEnabled = computed(() => {
        const q = resolvedQuery.value;
        if (!q) return false;
        return !!(q.estateId || q.rentingProcessId || q.mfhId || q.rentTenancyId);
    });

    const enabled = computed(() => {
        const extraEnabled = options?.enabled !== undefined ? unref(options.enabled) : true;
        return defaultEnabled.value && extraEnabled;
    });

    return useQuery<RentalContract[]>(
        ['rental-contracts', 'by-estate', queryOrEstateId, expandOpts],
        async () => {
            const { rentalContracts } = await fetchRentalContracts({ query: resolvedQuery.value, expandOpts });
            return rentalContracts.map(rc => new RentalContract(rc as unknown as IRentalContract));
        },
        {
            enabled,
            initialData: [] as RentalContract[],
        },
    );
};

export function usePatchRentalContract() {
    return useMutation<BaseRentalContract, Error, { rentalContractId: number; fields: PatchRentalContractMutationRequest }>(
        async ({ rentalContractId, fields }: { rentalContractId: number; fields: PatchRentalContractMutationRequest }) =>
            getClient('propx').patchRentalContract({ rentalContractId }, fields),
    );
}

export function useRentalContract(rentalContractId?: MaybeRef<number | undefined>, expandOpts?: RentalContractExpandOpts) {
    const enabled = computed(() => !!unref(rentalContractId));
    return useQuery<RentalContract>(
        ['rentalContract', rentalContractId],
        () =>
            getClient('propx')
                .getRentalContract({ rentalContractId: unref(rentalContractId)! }, { expandOpts })
                .then(rc => new RentalContract(rc as IRentalContract)),
        { enabled },
    );
}
